import { useState, useMemo } from "react";
import toast from "react-hot-toast";
import avatarsGrid from "../assets/images/fluxi-avatars-grid.png";
import { useAuth } from "../context/AuthContext";
import { dictionary } from "../utils/dictionary";
import { decodeTokenService } from "../services/decodeTokenService";
import { getCountriesList, getLanguagesList } from "../const/countryList";

const AVATAR_STORAGE_KEY = "user_profile_avatar";

const fluxiAvatars = [0, 1, 2, 3, 4, 5];

const UserProfilePage = () => {
  const { language, country, timezone, displayName } = useAuth();
  const t = (k: string) => dictionary[k]?.[language] || k;

  const email = decodeTokenService.getEmailFromToken() || "";
  const username = displayName;

  const countriesList = useMemo(() => getCountriesList(language), [language]);
  const languagesList = useMemo(() => getLanguagesList(language), [language]);
  const currentCountry = countriesList.find((c) => c.country === country);
  const currentLanguage = languagesList.find((l) => l.code === language);
  const currencyCode = currentCountry?.currency || "USD";
  const currencySymbol = currencyCode === "BRL" ? "R$" : "$";

  const [isEditingPersonal, setIsEditingPersonal] = useState(false);

  const [selectedAvatarIndex, setSelectedAvatarIndex] = useState<number>(() => {
    const v = localStorage.getItem(AVATAR_STORAGE_KEY);
    const n = v ? parseInt(v, 10) : 0;
    return Number.isFinite(n) && n >= 0 && n <= 5 ? n : 0;
  });

  const handleAvatarSelect = (index: number) => {
    setSelectedAvatarIndex(index);
  };

  const handleSavePersonal = () => {
    localStorage.setItem(AVATAR_STORAGE_KEY, String(selectedAvatarIndex));
    window.dispatchEvent(new Event("user-avatar-changed"));
    toast.success(t("profile-saved-success"));
  };

  const renderAvatar = (size: number, ringClass = "") => {
    return (
      <div
        className={`rounded-full border-2 border-[#E9EDF7] overflow-hidden ${ringClass}`}
        style={{
          width: size,
          height: size,
          backgroundImage: `url(${avatarsGrid})`,
          backgroundSize: "200% 300%",
          backgroundPosition: `${(selectedAvatarIndex % 2) * 100}% ${
            Math.floor(selectedAvatarIndex / 2) * 50
          }%`,
        }}
      />
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] px-16 pt-10 pb-16">
      <div>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">
            {t("profile-my-profile")}
          </h1>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(112,144,176,0.08)] p-6 md:p-8">
            {/* Avatar + Name + Edit */}
            <div className="flex items-center justify-between mb-[32px] flex-wrap gap-[20px]">
              <div className="flex items-center gap-[20px]">
                <button
                  onClick={() => setIsEditingPersonal(true)}
                  className="relative group"
                >
                  {renderAvatar(80, "group-hover:border-fluxi transition-all cursor-pointer")}
                  <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <svg
                      className="w-[24px] h-[24px] opacity-0 group-hover:opacity-100 transition-opacity"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </div>
                </button>
                <div>
                  <h2 className="text-[#2B3674] text-[22px] font-bold mb-[4px]">
                    {username}
                  </h2>
                  <p className="text-[#A3AED0] text-[14px]">
                    {t("profile-fluxi-user")}
                  </p>
                </div>
              </div>

              {isEditingPersonal && (
                <>
                  <div className="flex gap-[12px] items-center">
                    {fluxiAvatars.map((index) => (
                      <button
                        key={index}
                        onClick={() => handleAvatarSelect(index)}
                        className={`w-[56px] h-[56px] rounded-full overflow-hidden border-[3px] transition-all ${
                          selectedAvatarIndex === index
                            ? "border-fluxi ring-4 ring-fluxi/20 scale-110"
                            : "border-[#E9EDF7] hover:border-[#A3AED0] hover:scale-105"
                        }`}
                      >
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundImage: `url(${avatarsGrid})`,
                            backgroundSize: "200% 300%",
                            backgroundPosition: `${(index % 2) * 100}% ${
                              Math.floor(index / 2) * 50
                            }%`,
                          }}
                        />
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-[12px]">
                    <button
                      onClick={() => setIsEditingPersonal(false)}
                      className="px-[18px] py-[10px] bg-white border-2 border-[#E9EDF7] text-[#A3AED0] rounded-[12px] text-[14px] font-bold hover:border-[#A3AED0] hover:text-[#2B3674] transition-colors"
                    >
                      {t("profile-cancel")}
                    </button>
                    <button
                      onClick={() => {
                        handleSavePersonal();
                        setIsEditingPersonal(false);
                      }}
                      className="px-[20px] py-[10px] bg-fluxi text-white rounded-[12px] text-[14px] font-bold hover:bg-fluxi-dark transition-colors shadow-[0_4px_16px_rgba(67,104,229,0.3)]"
                    >
                      {t("profile-save")}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Account Info */}
            <div className="mb-[24px]">
              <h3 className="text-[#2B3674] text-[18px] font-bold mb-[6px]">
                {t("profile-account-info")}
              </h3>
              <p className="text-[#A3AED0] text-[13px]">
                {t("profile-account-info-desc")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-[28px] mb-[28px]">
              <div className="bg-[#F4F7FE] rounded-xl p-5">
                <label className="text-[#A3AED0] text-[11px] font-bold tracking-[0.1em] uppercase block mb-[12px]">
                  {t("profile-country")}
                </label>
                <div className="flex items-center gap-[12px]">
                  <span className="text-[32px] leading-none">
                    {currentCountry?.flag || "\uD83C\uDF0E"}
                  </span>
                  <div className="text-[#2B3674] text-[16px] font-bold">
                    {currentCountry?.name || country}
                  </div>
                </div>
              </div>

              <div className="bg-[#F4F7FE] rounded-xl p-5">
                <label className="text-[#A3AED0] text-[11px] font-bold tracking-[0.1em] uppercase block mb-[12px]">
                  {t("profile-currency")}
                </label>
                <div className="flex items-center gap-[12px]">
                  <div className="w-[32px] h-[32px] bg-fluxi rounded-full flex items-center justify-center">
                    <span className="text-white text-[16px] font-bold">
                      {currencySymbol}
                    </span>
                  </div>
                  <div className="text-[#2B3674] text-[16px] font-bold">
                    {currencyCode}
                  </div>
                </div>
              </div>

              <div className="bg-[#F4F7FE] rounded-xl p-5">
                <label className="text-[#A3AED0] text-[11px] font-bold tracking-[0.1em] uppercase block mb-[12px]">
                  {t("profile-language")}
                </label>
                <div className="flex items-center gap-[12px]">
                  <svg className="w-[32px] h-[32px] flex-shrink-0" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="14" stroke="#4368E5" strokeWidth="2" />
                    <path d="M16 2C16 2 11 8 11 16C11 24 16 30 16 30" stroke="#4368E5" strokeWidth="2" />
                    <path d="M16 2C16 2 21 8 21 16C21 24 16 30 16 30" stroke="#4368E5" strokeWidth="2" />
                    <line x1="2" y1="16" x2="30" y2="16" stroke="#4368E5" strokeWidth="2" />
                  </svg>
                  <div className="text-[#2B3674] text-[16px] font-bold">
                    {currentLanguage?.name || language}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-[28px]">
              <div className="bg-[#F4F7FE] rounded-xl p-5">
                <label className="text-[#A3AED0] text-[11px] font-bold tracking-[0.1em] uppercase block mb-[12px]">
                  {t("profile-email")}
                </label>
                <div className="flex items-center gap-[12px]">
                  <svg className="w-[32px] h-[32px] flex-shrink-0" viewBox="0 0 32 32" fill="none">
                    <rect x="4" y="8" width="24" height="16" rx="2" stroke="#4368E5" strokeWidth="2" />
                    <path d="M4 10L16 18L28 10" stroke="#4368E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="text-[#2B3674] text-[14px] font-semibold truncate">
                    {email}
                  </div>
                </div>
              </div>

              <div className="bg-[#F4F7FE] rounded-xl p-5">
                <label className="text-[#A3AED0] text-[11px] font-bold tracking-[0.1em] uppercase block mb-[12px]">
                  {t("profile-timezone")}
                </label>
                <div className="flex items-center gap-[12px]">
                  <svg className="w-[32px] h-[32px] flex-shrink-0" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="14" stroke="#4368E5" strokeWidth="2" />
                    <path d="M16 6V16L22 20" stroke="#4368E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="text-[#2B3674] text-[16px] font-bold truncate">
                    {timezone}
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
